import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Bell, BellOff, BellRing, CheckCircle, XCircle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import pushService from '../services/pushNotifications';

export default function NotificationSettings() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({
    supported: false,
    subscribed: false,
    permission: 'default'
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    const newStatus = await pushService.getSubscriptionStatus();
    setStatus(newStatus);
    setLoading(false);
  };

  const handleSubscribe = async () => {
    setActionLoading(true);
    const result = await pushService.subscribe(token);
    
    if (result.success) {
      toast.success(t('notifications.permissionGranted'));
      setStatus(prev => ({ ...prev, subscribed: true, permission: 'granted' }));
    } else {
      toast.error(result.error || t('notifications.permissionDenied'));
    }
    setActionLoading(false);
  };

  const handleUnsubscribe = async () => {
    setActionLoading(true);
    const result = await pushService.unsubscribe(token);
    
    if (result.success) {
      toast.success('Notifications désactivées');
      setStatus(prev => ({ ...prev, subscribed: false }));
    } else {
      toast.error(result.error);
    }
    setActionLoading(false);
  };

  const handleTestNotification = async () => {
    setActionLoading(true);
    const result = await pushService.sendTestNotification(token);
    
    if (result.message) {
      toast.success(result.message);
    } else {
      toast.error(result.error || 'Erreur envoi test');
    }
    setActionLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
          <Bell className="w-6 h-6 text-primary" />
          {t('notifications.title')}
        </h2>
        <p className="text-slate-600">Recevez des alertes instantanées sur votre navigateur</p>
      </div>

      {/* Status Card */}
      <Card className={status.subscribed ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
        <CardHeader>
          <div className="flex items-center gap-3">
            {status.subscribed ? (
              <BellRing className="w-8 h-8 text-green-600" />
            ) : (
              <BellOff className="w-8 h-8 text-amber-600" />
            )}
            <div>
              <CardTitle className="text-lg">
                Notifications Push
              </CardTitle>
              <CardDescription>
                {status.subscribed 
                  ? 'Vous recevrez des notifications sur cet appareil'
                  : 'Activez les notifications pour ne rien manquer'
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!status.supported ? (
            <div className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              <span>Votre navigateur ne supporte pas les notifications push</span>
            </div>
          ) : status.permission === 'denied' ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                <span>Les notifications sont bloquées</span>
              </div>
              <p className="text-sm text-slate-500">
                Pour activer les notifications, cliquez sur l'icône de cadenas dans la barre d'adresse 
                et autorisez les notifications pour ce site.
              </p>
            </div>
          ) : status.subscribed ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Activées</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestNotification}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Tester
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUnsubscribe}
                  disabled={actionLoading}
                  className="text-red-600 hover:bg-red-50"
                >
                  {t('notifications.disable')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-amber-700 font-medium">Non activées</span>
              <Button
                onClick={handleSubscribe}
                disabled={actionLoading}
                className="bg-primary hover:bg-primary-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                {t('notifications.enable')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info about notifications */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Vous serez notifié pour:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Nouvelles demandes de congé (admins)</li>
                <li>Réponses à vos demandes de congé</li>
                <li>Nouveaux justificatifs soumis (admins)</li>
                <li>Fiches de paie disponibles</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
